import { buildSaasHandoffResumeRequest } from './saasHandoffBootstrap';

type ResolvedHandoffPayload = {
  preferredOpenTarget?: {
    type?: string;
    id?: string;
    username?: string;
  };
  fallbackOpenTarget?: {
    type?: string;
    username?: string;
  };
};

const RESOLVED_HANDOFF_CACHE_TTL_MS = 5_000;
const resolvedHandoffPayloadByKey = new Map<string, { expiresAt: number; payload: ResolvedHandoffPayload }>();
const pendingResolvedHandoffByKey = new Map<string, Promise<ResolvedHandoffPayload | null>>();

function getResolvedHandoffCacheKey(resumeRequest: Record<string, unknown>) {
  return JSON.stringify(resumeRequest);
}

async function resolveResolvedHandoffPayload(
  cacheKey: string,
  resumeRequest: Record<string, unknown>,
  fetchFn: typeof fetch,
) {
  const now = Date.now();
  const cached = resolvedHandoffPayloadByKey.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return cached.payload;
  }

  const pending = pendingResolvedHandoffByKey.get(cacheKey);
  if (pending) {
    return pending;
  }

  const request = (async () => {
    const response = await fetchFn('/api/run/handoff/current', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(resumeRequest),
    });
    if (!response.ok) {
      return null;
    }

    const payload = await response.json() as ResolvedHandoffPayload;
    resolvedHandoffPayloadByKey.set(cacheKey, {
      expiresAt: Date.now() + RESOLVED_HANDOFF_CACHE_TTL_MS,
      payload,
    });
    return payload;
  })().finally(() => {
    pendingResolvedHandoffByKey.delete(cacheKey);
  });

  pendingResolvedHandoffByKey.set(cacheKey, request);
  return request;
}

export function resetResolvedSaasHandoffStateForTests() {
  resolvedHandoffPayloadByKey.clear();
  pendingResolvedHandoffByKey.clear();
}

export async function attemptResolvedSaasHandoffOpen(params: {
  canOpenResolvedTarget?: boolean;
  currentUserId?: string;
  clearResolvedHandoffState: () => void;
  clearDraft?: (payload: { chatId: string }) => void;
  fetchFn?: typeof fetch;
  hasResolvedTargetEntity?: (chatId: string) => boolean;
  openChat?: (payload: { id: string }) => void;
  openChatByUsername?: (payload: { username: string }) => void;
  onResolvedTargetOpen?: (payload: { chatId?: string; username?: string }) => void;
}) {
  const resumeRequest = buildSaasHandoffResumeRequest({ currentUserId: params.currentUserId });
  if (!resumeRequest) {
    return false;
  }

  try {
    const payload = await resolveResolvedHandoffPayload(
      getResolvedHandoffCacheKey(resumeRequest),
      resumeRequest,
      params.fetchFn ?? window.fetch,
    );
    if (!payload) {
      return false;
    }
    const preferredTarget = payload.preferredOpenTarget;
    if (params.canOpenResolvedTarget === false) {
      return false;
    }
    if (preferredTarget?.type === 'chat' && preferredTarget.id && typeof params.openChat === 'function') {
      const fallbackTarget = payload.fallbackOpenTarget;
      const shouldFallbackToUsername = Boolean(
        fallbackTarget?.username
        && typeof params.openChatByUsername === 'function'
        && params.hasResolvedTargetEntity
        && !params.hasResolvedTargetEntity(preferredTarget.id),
      );
      if (shouldFallbackToUsername) {
        params.openChatByUsername!({ username: fallbackTarget!.username! });
        params.onResolvedTargetOpen?.({
          chatId: preferredTarget.id,
          username: fallbackTarget!.username!,
        });
        params.clearResolvedHandoffState();
        return true;
      }

      params.openChat({ id: preferredTarget.id });
      params.clearDraft?.({ chatId: preferredTarget.id });
      params.onResolvedTargetOpen?.({ chatId: preferredTarget.id });
      params.clearResolvedHandoffState();
      return true;
    }

    const fallbackTarget = preferredTarget?.type === 'username'
      ? preferredTarget
      : payload.fallbackOpenTarget;
    if (fallbackTarget?.username && typeof params.openChatByUsername === 'function') {
      params.openChatByUsername({ username: fallbackTarget.username });
      params.onResolvedTargetOpen?.({ username: fallbackTarget.username });
      params.clearResolvedHandoffState();
      return true;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[run-saas-handoff] resume_open_failed', {
      message: err instanceof Error ? err.message : String(err),
    });
  }

  return false;
}
